import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, CreditCard, Landmark, Loader2, Lock, XCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { BrandLockup } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Option } from "@/pages/portal/PayPage";

const EXPIRED = "This payment link has expired — please sign in to pay";
const pub = axios.create({ baseURL: `${API}/paylink` });

export const Frame = ({ children }) => (
  <div className="min-h-screen" data-testid="paylink-layout">
    <header className="sidebar-surface grain relative border-b"><div className="relative mx-auto max-w-xl px-4 py-4"><BrandLockup compact /></div></header>
    <main className="mx-auto max-w-xl px-4 py-8">{children}</main>
  </div>
);

export const Expired = ({ message = EXPIRED, testid = "paylink-expired" }) => (
  <div className="rounded-lg border bg-card p-8 text-center" data-testid={testid}>
    <XCircle className="mx-auto h-12 w-12 text-destructive" />
    <h1 className="mt-4 text-xl font-semibold" data-testid={`${testid}-message`}>{message}</h1>
    <Button asChild className="mt-5" data-testid={`${testid}-sign-in-button`}><Link to="/login">Sign in</Link></Button>
  </div>
);

export function PayLinkPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [expired, setExpired] = useState(false);
  const [method, setMethod] = useState("ach");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { pub.get(`/${token}`).then((r) => setD(r.data)).catch(() => setExpired(true)); }, [token]);

  const go = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await pub.post(`/${token}/checkout`, { method, origin_url: window.location.origin });
      window.location.assign(data.checkout_url);
    } catch (e) {
      if (e.response?.status === 410) setExpired(true);
      setErr(e.response?.data?.detail?.message || "Couldn't start the payment. Please try again.");
      setBusy(false);
    }
  };

  if (expired) return <Frame><Expired /></Frame>;
  if (!d) return <Frame><div className="py-16 text-center"><Loader2 className="mx-auto animate-spin" /></div></Frame>;
  return (
    <Frame>
      <div data-testid="paylink-page">
        <div className="eyebrow" data-testid="paylink-loan-number">{d.loan_number} · Secure payment</div>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight" data-testid="paylink-greeting">Hi {d.first_name}, your payment is due</h1>
        <div className="gold-rule mb-6 mt-4 w-24" />
        <div className="mb-6 rounded-lg border bg-card p-5">
          <div className="eyebrow text-[10px]">Amount due</div>
          <div className="num mt-1 font-display text-4xl font-semibold" data-testid="paylink-amount-due">{money(d.amount_due_cents)}</div>
          {d.pending.map((t, i) => <div key={i} className="mt-2 text-xs text-muted-foreground">Bank payment of {money(t.amount_cents)} started {fmtDate(t.started_on)} · {t.message} (already excluded)</div>)}
        </div>
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary" data-testid="paylink-bank-tip"><Landmark className="h-4 w-4" /> Save time and fees: pay by bank account</p>
        <div className="space-y-3" role="radiogroup">
          <Option active={method === "ach"} onClick={() => setMethod("ach")} testid="paylink-method-ach"
            title={<span className="flex flex-wrap items-center gap-2">Pay by bank account — free <span data-testid="paylink-recommended-badge" className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">Recommended</span></span>}>
            <div className="text-xs text-muted-foreground">No fee. Usually clears in 3–5 business days.</div>
          </Option>
          <Option active={method === "card"} onClick={() => setMethod("card")} testid="paylink-method-card" title="Pay by card">
            <div className="text-xs text-muted-foreground">Debit or credit card. No fee.</div>
          </Option>
        </div>
        {err && <p className="mt-3 text-sm text-destructive" data-testid="paylink-error">{err}</p>}
        <Button onClick={go} disabled={busy || d.amount_due_cents < 100} className="mt-6 h-12 w-full text-base" data-testid="paylink-continue-button">
          {busy ? <Loader2 className="animate-spin" /> : method === "ach" ? <Landmark /> : <CreditCard />} Pay {money(d.amount_due_cents)} securely
        </Button>
        <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground"><Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This link only lets you pay this loan's amount due. Sign in to see your full account.</p>
      </div>
    </Frame>
  );
}

export function ResultView({ client, expiredMessage }) {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [s, setS] = useState(null);
  useEffect(() => {
    const cancelled = params.get("cancelled");
    const req = cancelled ? client.post(`/${token}/cancel`, { tx_id: cancelled }) : client.get(`/${token}/payments/${params.get("session_id")}`);
    req.then((r) => setS(r.data)).catch((e) => setS({ status: e.response?.status === 410 ? "expired_link" : "failed" }));
  }, [token, params, client]);
  if (!s) return <Frame><div className="py-16 text-center"><Loader2 className="mx-auto animate-spin" /></div></Frame>;
  if (s.status === "expired_link") return <Frame><Expired message={expiredMessage} /></Frame>;
  const paid = s.payment_status === "paid";
  const pending = s.status === "processing";
  const Icon = paid ? CheckCircle2 : pending ? Clock : XCircle;
  return (
    <Frame>
      <div className="rounded-lg border bg-card p-8 text-center" data-testid="paylink-result">
        <Icon className={`mx-auto h-12 w-12 ${paid ? "text-emerald-600" : pending ? "text-[hsl(var(--gold))]" : "text-destructive"}`} />
        <h1 className="mt-4 text-xl font-semibold" data-testid="paylink-result-message">{s.message || "Payment not completed — you have not been charged"}</h1>
        {s.amount_cents != null && <div className="num mt-2 font-display text-3xl font-semibold">{money(s.amount_cents)}</div>}
        <Button asChild variant="outline" className="mt-5"><Link to="/login">Sign in to your account</Link></Button>
      </div>
    </Frame>
  );
}

export const PayLinkResult = () => <ResultView client={pub} expiredMessage={EXPIRED} />;
